import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';

const UserBehaviourPage = () => {
  const [heatmapData, setHeatmapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchHeatmap();
  }, []);

  const fetchHeatmap = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/super-admin/app-analytics/user-action-heatmap`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setHeatmapData(response.data);
    } catch (err) {
      console.error('Failed to fetch heatmap:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading user behaviour analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">User Behaviour & Journey Metrics</h1>
          <p className="text-purple-200 text-sm">Mobile app user activity patterns and timing analytics</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button onClick={fetchHeatmap} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">User Behaviour & Journey Metrics</h1>
              <p className="text-purple-200 text-sm">Mobile app user activity patterns and timing analytics</p>
            </div>
          </div>
          <button onClick={fetchHeatmap} className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all flex items-center">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* User Action Heatmap */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">User Action Heatmap (Last 30 Days)</h3>
        <div className="overflow-x-auto">
          <div className="text-xs text-gray-600 mb-2">Activity by Day of Week and Hour</div>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="border border-gray-300 p-2 bg-gray-50 text-left text-xs font-semibold">Hour</th>
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => (
                  <th key={day} className="border border-gray-300 p-2 bg-gray-50 text-center text-xs font-semibold">{day}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(24)].map((_, hour) => (
                <tr key={hour}>
                  <td className="border border-gray-300 p-2 text-xs font-medium text-gray-700">
                    {hour.toString().padStart(2, '0')}:00
                  </td>
                  {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(day => {
                    const cell = heatmapData?.heatmap_data?.find(d => d.day === day && d.hour === hour);
                    const activity = cell ? cell.activity : 0;
                    const maxActivity = Math.max(...(heatmapData?.heatmap_data?.map(d => d.activity) || [1]));
                    const intensity = maxActivity > 0 ? activity / maxActivity : 0;
                    
                    let bgColor = 'bg-white';
                    if (intensity > 0.8) bgColor = 'bg-purple-700';
                    else if (intensity > 0.6) bgColor = 'bg-purple-500';
                    else if (intensity > 0.4) bgColor = 'bg-purple-400';
                    else if (intensity > 0.2) bgColor = 'bg-purple-200';
                    else if (intensity > 0) bgColor = 'bg-purple-100';
                    
                    return (
                      <td key={day} className={`border border-gray-300 p-2 text-center text-xs ${bgColor} ${intensity > 0.5 ? 'text-white' : 'text-gray-700'}`}>
                        {activity}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex items-center justify-end text-xs text-gray-600">
            <span className="mr-2">Low</span>
            <div className="flex space-x-1">
              <div className="w-4 h-4 bg-purple-100 border border-gray-300"></div>
              <div className="w-4 h-4 bg-purple-200 border border-gray-300"></div>
              <div className="w-4 h-4 bg-purple-400 border border-gray-300"></div>
              <div className="w-4 h-4 bg-purple-500 border border-gray-300"></div>
              <div className="w-4 h-4 bg-purple-700 border border-gray-300"></div>
            </div>
            <span className="ml-2">High</span>
          </div>
        </div>
        <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>What this shows:</strong> A heatmap showing when mobile app users are most active by day of week (columns) and hour of day (rows). Darker purple indicates higher activity across all app features (clock-ins, photos, expenses, overtime, holidays).
          </p>
          <p className="text-sm text-gray-700 mt-2">
            <strong>How to interpret:</strong> Dark cells during working hours (8am-6pm, Monday-Friday) are expected for a work app. Activity outside these hours may indicate overtime work, shift work, or weekend projects. Use this to schedule app maintenance during low-activity periods (light cells) and plan support availability during peak times. Unusual patterns (heavy weekend usage, late-night spikes) may indicate specific industry needs or user workflows worth investigating.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserBehaviourPage;